import {
  AbsenceType,
  AppointmentEventDto,
  ResourceDto,
  VisitCardLineStyleDto,
  QueryFactory,
  ResourceType,
  DataBlockVariant,
  useEnabledDataBlockTypes,
} from '@lmt/shared-types';
import {
  AppointmentPopupProps,
  MeasurementInfo,
} from '@lmt/unicorn-shared/app/components/calendar/devextreme/components/AppointmentPopup';
import { AppointmentPatientInfo } from '@lmt/unicorn-shared/app/components/calendar/devextreme/components/types';
import { VisitContent } from '@lmt/unicorn-shared/app/components/calendar/devextreme/components/VisitContent';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import AbsenceTypeIcon from '@lmt/unicorn-shared/app/icons/absence_type.svg?react';
import React, { MouseEventHandler, useMemo } from 'react';
import styles from './ResourceViewScheduler.module.scss';
import { emptyArray } from '@lmt/unicorn-shared/app/helpers/common';
import { Appointment } from '@lmt/unicorn-shared/app/common/devexpress/Scheduler/dx-scheduler';
import { useDataBlockTypeTranslation } from '../../../measurements/common';

export type AppointmentItem = {
  index: number;
  onAppointmentEnter?: (appointment: AppointmentPopupProps) => void;
  onMouseOver?: MouseEventHandler<HTMLElement>;
  onMouseLeave?: MouseEventHandler<HTMLElement>;
  data: {
    appointmentData: Appointment & {
      data: AppointmentEventDto;
      patient?: AppointmentPatientInfo;
      color: string;
      lineStyles: VisitCardLineStyleDto[];
      title: string;
      isAnonymized: boolean;
      subtenantCode: string;
      id: number | string;
      absenceType?: AbsenceType;
    };
    targetedAppointmentData: Appointment & { data: AppointmentEventDto };
  };
};
export const ResourceViewAppointment = React.memo(function (
  item: AppointmentItem,
) {
  const resourcesQuery = QueryFactory.ResourceQuery.useGetResourcesQuery({
    includeDeleted: false,
  });
  const resources = resourcesQuery.data?.data ?? emptyArray();
  const i18next = useScopedTranslation('absences');

  const eventDto = item.data.appointmentData.data;

  const absenceType = item.data.appointmentData.absenceType;
  if (absenceType) {
    const currentItemResource = resources[0];
    return (
      <div className={styles.absenceContent} data-test-id="absence-content">
        <div className={styles.resourceHeader}>
          <AbsenceTypeIcon className={styles.resourceIcon} />
          <span className={styles.resourceHeaderText}>
            {currentItemResource?.type === ResourceType.Doctors
              ? i18next.t('doctor_unavailable')
              : i18next.t('resource_unavailable')}
          </span>
        </div>
      </div>
    );
  }

  const patient = item.data.appointmentData.patient;
  const subtenantCode = item.data.appointmentData.subtenantCode;
  const measurements = useVisitDeviceMeasurements(
    item.data.appointmentData.data?.id,
  );

  if (item.data.appointmentData.isAnonymized) {
    return <div className={styles.hatchedAppointmentContent} />;
  }

  return (
    <div
      className={styles.appointmentContent}
      onMouseOver={item.onMouseOver}
      onMouseLeave={item.onMouseLeave}
      onMouseEnter={() => {
        const { doctor, room, specialConditions } =
          getAppointmentResourceDetails(eventDto, resources);

        item.onAppointmentEnter?.({
          appointmentData: eventDto as AppointmentEventDto,
          patient: patient,
          doctor: doctor,
          room: room,
          resources: specialConditions,
          subtenantCode: subtenantCode,
          measurements: measurements,
        });
      }}
    >
      <VisitContent data={item.data.appointmentData} />
    </div>
  );
});

export function useAppointmentResourceDetails(eventDto: AppointmentEventDto): {
  resources: ResourceDto[];
  doctor: ResourceDto | undefined;
  room: ResourceDto | undefined;
  specialConditions: ResourceDto[] | undefined;
} {
  const resourcesQuery = QueryFactory.ResourceQuery.useGetResourcesQuery({
    includeDeleted: false,
  });
  const resources = resourcesQuery.data?.data ?? emptyArray();
  return useMemo(
    () => getAppointmentResourceDetails(eventDto, resources),
    [resources, eventDto],
  );
}

export function useVisitDeviceMeasurements(
  visitId: number | undefined,
): MeasurementInfo[] {
  const translateDataBlockType = useDataBlockTypeTranslation();
  const measurementQuery = QueryFactory.DataBlockQuery.useGetQuery(
    {
      variant: DataBlockVariant.DeviceMeasurement,
      visitId: visitId,
    },
    {
      enabled: visitId !== undefined,
    },
  );
  const dataBlockTypes = useEnabledDataBlockTypes();
  const measurements =
    visitId === undefined ? [] : (measurementQuery.data?.data ?? emptyArray());
  return useMemo(
    () =>
      measurements.map((dataBlock) => {
        return {
          id: dataBlock.id,
          caseId: dataBlock.caseId!,
          name: translateDataBlockType(
            dataBlockTypes.find((x) => x.id === dataBlock.typeId)!,
          ) as string,
        };
      }) ?? [],
    [measurements],
  );
}

export function getAppointmentResourceDetails(
  eventDto: AppointmentEventDto,
  allResources: ResourceDto[],
): {
  resources: ResourceDto[];
  doctor: ResourceDto | undefined;
  room: ResourceDto | undefined;
  specialConditions: ResourceDto[] | undefined;
} {
  const resources = (() => {
    if (!eventDto || !allResources?.length || !eventDto.resourceIds) {
      return [];
    }
    const ids = Array.isArray(eventDto.resourceIds)
      ? eventDto.resourceIds
      : [eventDto.resourceIds];
    if (!eventDto.resourceIds?.length) return [];

    return allResources.filter((x) => ids.includes(x.id));
  })();

  const room = resources.find((x) => x.type === ResourceType.Places);
  const doctor = resources.find((x) => x.type === ResourceType.Doctors);
  const specialConditions = resources.filter(
    (x) => x.type === ResourceType.SpecialConditions,
  );

  return {
    room,
    doctor,
    resources,
    specialConditions,
  };
}
